import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
}

export default function TradePasswordSheet({ open, onClose, onSuccess, title = '操作密码验证' }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setLoading(false);
      return;
    }
    // 检查是否设置了操作密码
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase
          .from('profiles')
          .select('trade_password')
          .eq('id', user.id)
          .maybeSingle();
        setHasPassword(!!data?.trade_password);
      } catch {
        // ignore
      }
    })();
  }, [open]);

  const handleVerify = useCallback(async () => {
    if (password.length !== 6) {
      showToast('请输入6位操作密码', 'error');
      return;
    }
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('登录已失效');
      const { data } = await supabase
        .from('profiles')
        .select('trade_password')
        .eq('id', user.id)
        .maybeSingle();

      if (!data?.trade_password) {
        showToast('请先设置操作密码', 'error');
        setLoading(false);
        return;
      }

      if (data.trade_password !== password) {
        showToast('操作密码错误', 'error');
        setPassword('');
        setLoading(false);
        return;
      }

      setPassword('');
      onSuccess();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '验证失败', 'error');
      setLoading(false);
    }
  }, [password, onSuccess, showToast]);

  const handleGoSettings = useCallback(() => {
    onClose();
    navigate('/profile/settings');
  }, [onClose, navigate]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end justify-center">
      <div className="bg-white rounded-t-2xl w-full p-5 animate-slide-up">
        <h3 className="text-brown-900 text-base font-bold text-center mb-4">{title}</h3>

        {!hasPassword ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-amber-500 text-2xl"></i>
            </div>
            <p className="text-brown-600 text-sm">您尚未设置操作密码</p>
            <p className="text-brown-400 text-xs mt-1">设置后即可进行敏感操作</p>
            <button
              onClick={handleGoSettings}
              className="mt-4 w-full py-3 rounded-xl bg-gold-500 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              去设置操作密码
            </button>
          </div>
        ) : (
          <>
            <p className="text-brown-500 text-xs text-center mb-4">请输入6位数字操作密码以确认</p>
            <div
              className="flex justify-center gap-2 mb-5 relative cursor-text"
              onClick={() => inputRef.current?.focus()}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-colors pointer-events-none ${
                    i < password.length
                      ? 'border-gold-400 bg-gold-50 text-brown-900'
                      : 'border-gray-200 text-transparent'
                  }`}
                >
                  {i < password.length ? '●' : ''}
                </div>
              ))}
              <input
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-text"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && password.length === 6) {
                    handleVerify();
                  }
                }}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || password.length !== 6}
                className={`flex-1 py-3 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap ${
                  loading || password.length !== 6
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
                }`}
              >
                {loading ? '验证中...' : '确认'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}